import { createFileRoute } from "@tanstack/react-router";
import { useInfiniteRecordsGermanWord } from ":entities/german-word";
import { WordSearch } from ":features/dictionary/dictionary/ui/word-search";
import { GermanWordList } from ":widgets/browser/ui/german-word-list";
import { ActiveFilterChips } from ":widgets/browser/ui/active-filter-chips";
import { useRecordsTag, type TagIdentifiable } from ":entities/tag";
import { useCallback, Suspense } from "react";
import { database } from ":shared/database/connection";
import {
	deleteGermanWords,
	getRelatedTablesForDeleteGermanWords,
} from ":widgets/browser/lib/delete";
import { AutocompleteTagField } from ":widgets/tag/ui/autocomplete-tag-field/tag-field";
import { useCreateTag } from ":widgets/tag/ui/autocomplete-tag-field/use-get-or-create-tag";
import { GermanWordBulkTextarea } from ":widgets/browser/ui/german-word-bulk-textarea";
import {
	Disclosure,
	DisclosurePanel,
	DisclosureTrigger,
} from "@simurgh/feathers/ui/disclosure";
import { z } from "zod/mini";
import { WordListSkeleton } from ":shared/ui/skeleton";
import { useTranslation } from "react-i18next";

const browserSearchSchema = z.object({
	query: z.optional(z.string()),
	tagId: z.optional(z.array(z.string())),
});

export const Route = createFileRoute("/$locale/dashboard/browser")({
	validateSearch: browserSearchSchema,
	component: BrowserComponent,
});

function BrowserComponent() {
	const { t } = useTranslation();
	const navigate = Route.useNavigate();
	const search = Route.useSearch();
	const queryParam = search.query || "";
	const tagIdParams = search.tagId || [];

	const { data: tagRecords = [] } = useRecordsTag({
		filters: tagIdParams.length ? { id: tagIdParams } : {},
		enabled: tagIdParams.length > 0,
	});

	const onTagInsert = useCallback(
		async (tag: TagIdentifiable) => {
			const tagIds = [...tagIdParams, tag.id];
			navigate({
				search: (prev: typeof search) => ({
					...prev,
					tagId: tagIds,
				}),
			});
		},
		[tagIdParams, navigate],
	);

	const onTagRemove = useCallback(
		async (tagId: TagIdentifiable["id"]) => {
			const tagIds = tagIdParams.filter((item: string) => item !== tagId);
			navigate({
				search: (prev: typeof search) => ({
					...prev,
					tagId: tagIds.length > 0 ? tagIds : undefined,
				}),
			});
		},
		[tagIdParams, navigate],
	);

	const { mutate: onCreateTag } = useCreateTag({
		onSuccess: (tag) => onTagInsert(tag),
	});

	const chips: { key: string; label: string; onRemove: () => void }[] = [];
	if (queryParam) {
		chips.push({
			key: "query",
			label: queryParam,
			onRemove: () => {
				navigate({
					search: (prev: typeof search) => ({
						...prev,
						query: undefined,
					}),
				});
			},
		});
	}
	if (tagIdParams.length) {
		tagIdParams.forEach((id: string) => {
			const label = tagRecords.find((t) => t.id === id)?.label || id;
			chips.push({
				key: "tagId",
				label,
				onRemove: () => {
					const tagIds = tagIdParams.filter((tid: string) => tid !== id);
					navigate({
						search: (prev: typeof search) => ({
							...prev,
							tagId: tagIds.length > 0 ? tagIds : undefined,
						}),
					});
				},
			});
		});
	}

	const { data, hasNextPage, fetchNextPage, isFetchingNextPage, isPending } =
		useInfiniteRecordsGermanWord({
			filters: {
				...(queryParam ? { query: queryParam } : {}),
				...(tagIdParams.length ? { tagId: tagIdParams } : {}),
			},
			notFilters: {},
		});

	return (
		<>
			<title>
				{t("pages.browser.title")} - {t("app.name")}
			</title>
			<meta name="description" content={t("pages.browser.description")} />
			<div className="mb-3">
				<div className="flex flex-col md:flex-row gap-4 md:gap-6">
					<WordSearch
						className="flex-1"
						onWordSelected={() => {}}
						onDeleteGermanWord={async (id: string) => {
							await database.transaction(
								"rw",
								getRelatedTablesForDeleteGermanWords(),
								async () => await deleteGermanWords({ germanWordId: id }),
							);
						}}
					/>
					<AutocompleteTagField
						className="flex-1"
						label={t("forms.labels.filterByTags")}
						placeholder={t("forms.placeholders.typeToSearchSelectTags")}
						name="filterTags"
						selectedTags={tagIdParams}
						onCreate={onCreateTag as any}
						onInsert={onTagInsert}
						onRemove={onTagRemove}
					/>
				</div>
				<div className="mt-4">
					<Disclosure>
						<DisclosureTrigger>
							<div>
								<div className="font-medium text-start">
									{t("pages.browser.bulkImport")}
								</div>
								<div className="text-sm text-muted-fg">
									{t("pages.browser.bulkImportDescription")}
								</div>
							</div>
						</DisclosureTrigger>
						<DisclosurePanel>
							<GermanWordBulkTextarea />
						</DisclosurePanel>
					</Disclosure>
				</div>
			</div>
			<ActiveFilterChips chips={chips} />
			<Suspense fallback={<WordListSkeleton />}>
				<GermanWordList
					data={data}
					fetchNextPage={fetchNextPage}
					hasNextPage={hasNextPage}
					isFetchingNextPage={isFetchingNextPage}
					isLoading={isPending}
				/>
			</Suspense>
		</>
	);
}
